import {
  AfterViewChecked,
  Component,
  ElementRef, EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ValueProvider,
  ViewChild
} from '@angular/core';
import {
  AudioService, AudioSettingsModel,
  BulletFormatListModel, CountService,
  EmojiPickerService, EmojiSettingsModel,
  FormatPainterService,
  HtmlEditorService,
  ImageService,
  LinkService, MarkdownEditorService, NodeSelection,
  NumberFormatListModel,
  PasteCleanupService,
  QuickToolbarService,
  ResizeArgs,
  ResizeService,
  RichTextEditorComponent, TableService,
  ToolbarService,
  ToolbarType, VideoService
} from "@syncfusion/ej2-angular-richtexteditor";
import {
  BackgroundColorModel, FontColorModel, FontFamilyModel,
  IFrameSettingsModel, ImageSettingsModel, InlineModeModel, QuickToolbarSettingsModel,
  ToolbarSettingsModel, VideoSettingsModel
} from "@syncfusion/ej2-richtexteditor/src/rich-text-editor/models/models";
import {BeforeUploadEventArgs, RemovingEventArgs, SelectedEventArgs, UploadingEventArgs} from "@syncfusion/ej2-inputs";
import {
  ActionBeginEventArgs,
  ActionCompleteEventArgs,
  AfterImageDeleteEventArgs, ChangeEventArgs,
  ImageDropEventArgs
} from "@syncfusion/ej2-richtexteditor/src/rich-text-editor/base/interface";

import * as prettier from "prettier";
import parserHtml from "prettier/parser-html";
import {SplitterComponent} from "@syncfusion/ej2-angular-layouts";

// TODO: we could replace code mirror with monaco
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/css/css.js';
import 'codemirror/mode/htmlmixed/htmlmixed.js';
import {CodemirrorComponent} from "@ctrl/ngx-codemirror";
import {UtilService} from "../../services/util/util.service";
import {UploaderComponent} from "@syncfusion/ej2-angular-inputs";
import {ClickEventArgs} from "@syncfusion/ej2-navigations";
import {BeforeOpenEventArgs, BeforeSanitizeHtmlArgs, DialogComponent} from "@syncfusion/ej2-angular-popups";
import {UserService} from "../../services/user/user.service";
import {Subscriber} from "rxjs";


// Notes for image resize
//
// <!--resizing: EmitType<ResizeArgs>;-->
// <!--  /**-->
// <!--  * Triggers only when start resize the image.-->
// <!--  *-->
// <!--  * @event 'object'-->
// <!--  * @blazorProperty 'OnResizeStart'-->
// <!--  */-->
// <!--  resizeStart: EmitType<ResizeArgs>;-->
// <!--    /**-->
// <!--    * Triggers only when stop resize the image.-->
// <!--    *-->
// <!--    * @event 'object'-->
// <!--    * @blazorProperty 'OnResizeStop'-->
// <!--    */-->
// <!--    resizeStop: EmitType<ResizeArgs>;-->


@Component({
  selector: 'sb-rtf-editor',
  templateUrl: './rtf-editor.component.html',
  styleUrls: ['./rtf-editor.component.scss'],
  providers: [
    AudioService,
    CountService,
    EmojiPickerService,
    FormatPainterService,
    HtmlEditorService,
    ImageService,
    LinkService,
    MarkdownEditorService,
    PasteCleanupService,
    QuickToolbarService,
    ResizeService,
    TableService,
    ToolbarService,
    VideoService,
  ]
})
export class RtfEditorComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChild('editor') public editor: RichTextEditorComponent;

  @Input()
  get value(): string {
    return this._value;
  }
  set value(value: string) {
    this._value = value;
  }
  private _value: string = "";

  @Input()
  rendererProperties: object;

  @Input()
  record;

  @Input()
  fieldDefinition;

  @Input()
  fieldViewModel;

  @Input()
  onChangeCommand: (value: string) => void;

  @Input()
  saveCommand: () => void;

  @Input()
  readOnly: boolean = false;

  @Input()
  mirror: boolean = false;

  @Input()
  preview: boolean = false;

  @Input()
  height: any;

  @Input()
  minHeight: any = "50px";

  @Input()
  maxHeight: any = "300px";

  @Input()
  cssClass: string;

  @Input()
  enableHtmlSanitizer: boolean = false;

  cssClassBase = "sb-rtf-editor";

  editorId: string;
  uploaderId: string;

  editorInit = false;

  saveInterval = 1000; // 1 sec in ms

  autoSaveOnIdle = false;

  showCharCount = true;

  editorHeight = "600px"; // This should depend on the container

  toolbarSettings: ToolbarSettingsModel = {
    enable: true,
    enableFloating: false,
    type: ToolbarType.Scrollable,
    items: [
      "Bold",
      "Italic",
      "Underline",
      "Formats",
      "FontSize",
      "FontColor",
      "BackgroundColor",
      {
        tooltipText: "Emoji Picker",
        template: `<button type="button" class="e-tbar-btn e-btn" tabindex="-1" id="editorId_toolbar_custom_AttachFile" style="width:100%"><div class="e-tbar-btn-text">
                    <i class="e-btn-icon e-emoji e-icons"></i></div></button>`,
        command: "EmojiPicker",
        undo: true,
      },
      "Indent",
      "Outdent",
      "Alignments",
      "NumberFormatList",
      "BulletFormatList",
      "|",
      "CreateTable",
      "Image",
      "Audio",
      "Video",
      "CreateLink",
      {
        tooltipText: "Attach file",
        template: `<button type="button" class="e-tbar-btn e-btn" tabindex="-1" id="editorId_toolbar_custom_AttachFile" style="width:100%"><div class="e-tbar-btn-text">
                    <i class="e-icons e-upload-1"></i></div></button>`,
        command: "AttachFile",
        undo: true,
        click: this.onAttachFile.bind(this)
      },
      "|",
      "SourceCode",
      "|",
      "FullScreen",
    ],
    itemConfigs: {
      minimize: {
        icon: "e-close"
      }
    }
  };

  private boundContentChangedHandler = this.contentChangedHandler.bind(this);

  numberFormatList: NumberFormatListModel = {
    types: [
      {text: 'None', value: 'none'},
      {text: 'Number', value: 'decimal'},
      {text: 'Lower Greek', value: 'lowerGreek'},
      {text: 'Lower Roman', value: 'lowerRoman'},
      {text: 'Upper Alpha', value: 'upperAlpha'},
      {text: 'Lower Alpha', value: 'lowerAlpha'},
      {text: 'Upper Roman', value: 'upperRoman'},
    ]
  };

  bulletFormatList: BulletFormatListModel = {
  };

  iframeSettings: IFrameSettingsModel = {
    enable: false,
  };

  // Determines if the toolbar appears when something is selected or rg editor is clicked. If enabled then the
  // top-level toolbar is not visible.
  inlineMode: InlineModeModel = {
    enable: false,
    onSelection: true
  };

  quickToolbarSettings: QuickToolbarSettingsModel = {
    enable: true,
    showOnRightClick: false,
    actionOnScroll: 'hide',
    link: [
      'Open', 'Edit', 'UnLink',
    ],
    image: [
      'Replace', 'Align', 'Caption', 'Remove',
      '-',
      'InsertLink','OpenImageLink', 'EditImageLink', 'RemoveImageLink', 'Display', 'AltText', 'Dimension'
    ],
    audio: [
      'AudioReplace', 'AudioRemove', 'AudioLayoutOption'
    ],
    video: [
      'VideoReplace', 'VideoAlign', 'VideoRemove', 'VideoLayoutOption', 'VideoDimension'
    ],
    text:[
      'Bold', 'Italic', 'Underline', 'StrikeThrough', 'BackgroundColor', 'FontColor', 'FontSize',
      '-',
      'SuperScript', 'SubScript', 'LowerCase', 'UpperCase', 'FormatPainter', 'ClearFormat'
    ],
    table: [
      'TableHeader', 'TableRows', 'TableColumns', 'BackgroundColor', 'TableCell',
      '-',
      'TableRemove', 'Alignments', 'TableCellHorizontalAlign', 'TableCellVerticalAlign', 'Styles', 'TableEditProperties',
    ],
  };

  backgroundColor: BackgroundColorModel = {
    modeSwitcher: true
  };

  fontColor: FontColorModel = {
    modeSwitcher: true
  };

  fontFamily: FontFamilyModel = {
    default: 'Poppins',
    items: [
      {
        text: "Poppins",
        value: "Poppins",
        cssClass: "poppins-style",
        command: "Font",
        subCommand: "FontName"
      }
    ]
  };

  insertImageSettings: ImageSettingsModel = {
    allowedTypes: ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    resize: true,
    resizeByPercent: true,
    path: "/attachment/",
    saveUrl: '/attachment/uploadSF',
    removeUrl: 'attachment/removeSF'
  };

  insertAudioSettings: AudioSettingsModel = {
    path: "/attachment/",
    saveUrl: '/attachment/uploadSF',
    removeUrl: 'attachment/removeSF'
  };

  insertVideoSettings: VideoSettingsModel = {
    resize: true,
    resizeByPercent: true,
    path: "/attachment/",
    saveUrl: '/attachment/uploadSF',
    removeUrl: 'attachment/removeSF'
  };

  // Mirror support

  @ViewChild('splitter')
  splitter: SplitterComponent;

  @ViewChild('codeMirror')
  codeMirror: CodemirrorComponent;

  codemirrorOptions = {
    lineNumbers: true,
    mode: 'htmlmixed',
    readOnly: true,
  };

  mirrorValue: string = "";

  // Uploader support

  @ViewChild('uploader')
  public uploader: UploaderComponent;

  @ViewChild('uploaderDialog')
  public uploaderDialog: DialogComponent;

  uploaderVisible = false;

  uploaderTitle = "Attach files";

  cleanupResizeElements$: Subscriber<any>;

  constructor(private hostElement: ElementRef<HTMLElement>,
              private userService: UserService,
              private utilService: UtilService) { }

  ngOnInit(): void {
    // We can have multiple of these on a page so we generate unique ids.
    const suffix = new Date().getTime();
    this.editorId = `editorId-${suffix}`;
    this.uploaderId = `uploaderId-${suffix}`;
  }

  ngAfterViewChecked(): void {
    if (!this.editorInit && this.editor) {
      this.editorInit = true;
      const panel = this.editor.contentModule.getPanel() as HTMLElement;

      const self = this;

      // Support some height use cases. The default is suitable for an editor on a form.
      // Note that height includes toolbar, while minHeight and maxHeight only apply to the expandable content area.
      // The toolbar is about 40px high

      const minHeightMap = {
        'tiny': '30px',
        'small': '50px',
        'medium': '300px',
        'large': '500px',
      };

      const maxHeightMap = {
        'tiny': '150px',
        'small': '300px',
        'medium': '500px',
        'large': '800px',
      };

      const heightMap = {
        'tiny': '150px',
        'small': '300px',
        'medium': '500px',
        'large': '800px',
      };

      const rendererHeight = this.rendererProperties?.['editor.height'];
      const rendererMinHeight = this.rendererProperties?.['editor.minHeight'];
      const rendererMaxHeight = this.rendererProperties?.['editor.maxHeight'];

      const calculatedRendererHeight = heightMap[rendererHeight] ?? rendererHeight;
      const calculatedRendererMinHeight = minHeightMap[rendererMinHeight] ?? rendererMinHeight;
      const calculatedRendererMaxHeight = maxHeightMap[rendererMaxHeight] ?? rendererMaxHeight;

      if (this.height ?? calculatedRendererHeight) {
        this.editor.height = calculatedRendererHeight ?? this.height;
      } else {
        if (this.minHeight ?? calculatedRendererMinHeight) {
          panel.style.minHeight = calculatedRendererMinHeight ?? this.minHeight;
        }
        if (this.maxHeight ?? calculatedRendererMaxHeight) {
          panel.style.maxHeight = calculatedRendererMaxHeight ?? this.maxHeight;
        }
      }

      // In read only mode we hide everything except the content.
      if (this.readOnly) {
        this.editor.readonly = true;
        this.editor.toolbarSettings.enable = false;
        this.editor.showCharCount = false;
      }

      this.editor.cssClass = (this.cssClass ? this.cssClass + " " : "") + this.cssClassBase;

      // Ctrl-s or Cmd-s will cause the save.
      this.editor.contentModule.getDocument().addEventListener("keydown", (e: any): void => {
        if (e.key === 's' && (e.ctrlKey === true || e.metaKey === true)) { // meta is cmd on macOS
          e.preventDefault();
          self.editor.updateValue();
          self.executeOnChangeCommand(self.editor.value);
          this.executeSaveCommand();
        }
      });

      // The TableCommand.removeEmptyNode() sometimes removes the entire table contents so we replace it with a no-op
      (this.editor as any).properties['formatter'].editorManager.tableObj.removeEmptyNode = () => {
        console.debug("Patched TableCommand.removeEmptyNode()");
      }

      // SF bug - The cleanupResizeElements function is meant to be an EventEmitter - it stops the MSWord paste
      // from completing and so images are not converted.
      const originalCleanupResizeElements = (this.editor as any).cleanupResizeElements; // Private method
      const ed = (this.editor as any);
      ed.cleanupResizeElements = new EventEmitter();
      this.cleanupResizeElements$ = ed.cleanupResizeElements.subscribe((x) => {
        originalCleanupResizeElements.bind(this.editor)(x);
      });

      this.utilService.tickThen(() => {
        // The toolbar sometimes doesn't resize correctly, so it appears wider than the content area.
        this.editor.refreshUI();
        (this.editor.toolbarModule as any)?.refreshToolbar(); // Private method
        this.editor.refreshUI();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // SF bug - make sure the event handlers are removed
    (this.editor.imageModule as any)?.cancelResizeAction();

    // SF bug - make sure the event handlers are removed
    (this.editor.tableModule as any)?.cancelResizeAction();

    this.cleanupResizeElements$ && this.cleanupResizeElements$.unsubscribe();
  }

  executeOnChangeCommand(value: string) {
    this.onChangeCommand && this.onChangeCommand(value);
  }

  executeSaveCommand() {
    this.saveCommand && this.saveCommand();
  }

  onBeforeSanitizeHtml(e: BeforeSanitizeHtmlArgs) {
    const enabled = this.userService.getBooleanProperty('knowledge.article.iframe.enabled', false);
    if (enabled) {
      e.selectors.tags = e.selectors.tags.filter((t) => !t.includes('iframe'));
    }
  }

  onCreate(e: any) {
  }

  onToolbarClick($event: any) {
    if ($event?.item?.command == "SourceCode" && $event?.item?.subCommand == "SourceCode") {
      // Format the code so that editing in source code view is easier.
      // This can't work because SF strips newlines from 'value' in 'updateTextNode' in 'ej2-richtexteditor.es5.js'
      this.value = this.formatCode(this.value);
    } else if ($event?.item?.command == "EmojiPicker") {
      // BUG: The EmojiPicker seems to display in page coordinates so if you are scrolled down a document
      // it is below the viewport. We explicitly show it at a suitable place.
      // BUG: if the EmojiPicker is displayed explicitly then the search box is not displayed.
      const event = $event.originalEvent;
      const x = event.offsetX + 300;
      const y = event.offsetY + 40;
      this.editor.showEmojiPicker(x, y);
    }
  }

  onActionBegin($event: ActionBeginEventArgs) {
  }

  onActionComplete($event: ActionCompleteEventArgs) {
    // if ($event.requestType == 'Maximize') {
    //   const siteHeader = document.getElementsByClassName("site-header")[0] as HTMLElement;
    //   if (siteHeader) {
    //     siteHeader.style.display = 'none';
    //   }
    // } else if ($event.requestType == 'Minimize') {
    //   const siteHeader = document.getElementsByClassName("site-header")[0] as HTMLElement;
    //   if (siteHeader) {
    //     siteHeader.style.display = null;
    //   }
    // }
  }

  onChange($event: any) {
    const mirrorValue = this.editor.value ?? "";
    this.mirrorValue = this.formatCode(mirrorValue);
    this.executeOnChangeCommand(this.editor.value);
  }

  // This is used for audio/video upload
  onFileUploading($event: UploadingEventArgs) {
    // Send some parameters so that the attachment has the correct field values.

    // Values for testing a standalone editor
    const standaloneTableId = '0a01010a40cc1dde8140cc9dfcff00a7'; // User
    const standaloneParentTable = 'User';
    const standaloneFieldName = 'FirstName';

    const tableId = this.record?.id() || standaloneTableId;
    const parentTable = this.record?.TableClass?.value() || standaloneParentTable;
    const fieldName = this.fieldDefinition?.fieldName || standaloneFieldName;

    $event.customFormData = [
      { tableId: tableId},
      { parentTable: parentTable},
      { fieldName: fieldName},
    ];
  }

  // This is used for audio/video upload
  onFileUploadSuccess($event: any) {
    // Retrieve the image id from the x-record-id header and use it as part of the src parameter of the img tag.
    const id = $event.response.headers.split("\r\n").filter((x) => x.includes("x-record-id"))[0].split(": ")[1];

    if ($event.element) {
      // Drag-n-drop of image
      $event.element.src = "/attachment/" + id;
      $event.element.setAttribute('alt', $event.file.name);
      this.boundContentChangedHandler(); // Needed otherwise drag-n-drop images do not trigger content change
      $event.file.name = id; // Needed for paste of screenshot
    } else {
      // Select image from popup
      $event.file.name = id;
    }
  }

  onImageSelected($event: SelectedEventArgs) {
  }

  onBeforeImageUpload($event: BeforeUploadEventArgs) {
  }

  onBeforeImageDrop($event: ImageDropEventArgs) {
  }

  // This is for image uploading
  onImageUploading($event: UploadingEventArgs) {
    // Check that the mime type is correct and pass it in custom data if not
    // TODO: this may not be correct
    let realContentType = null;
    // let type = $event.fileData.type;
    // if (!type.startsWith("image/")) {
    //   realContentType = `image/${type}`;
    // }

    // Send some parameters so that the attachment has the correct field values.

    // Values for testing a standalone editor
    const standaloneTableId = '0a01010a40cc1dde8140cc9dfcff00a7'; // User
    const standaloneParentTable = 'User';
    const standaloneFieldName = 'FirstName';

    const tableId = this.record?.id() || standaloneTableId;
    const parentTable = this.record?.TableClass?.value() || standaloneParentTable;
    const fieldName = this.fieldDefinition?.fieldName || standaloneFieldName;

    $event.customFormData = [
      { tableId: tableId },
      { parentTable: parentTable },
      { fieldName: fieldName },
      { realContentType: realContentType }
    ];
  }

  // This is for image uploading
  onImageUploadSuccess($event: any) {
    // Retrieve the image id from the x-record-id header and use it as part of the src parameter of the img tag.
    const id = $event.response.headers.split("\r\n").filter((x) => x.includes("x-record-id"))[0].split(": ")[1];

    if ($event.element) {

      // Fix opacity problem
      $event.element.style.opacity = 1;

      // Drag-n-drop of image
      $event.element.src = "/attachment/" + id;
      $event.element.setAttribute('alt', $event.file.name);
      this.boundContentChangedHandler(); // Needed otherwise drag-n-drop images do not trigger content change
      $event.file.name = id; // Needed for paste of screenshot
    } else {
      // Select image from popup
      $event.file.name = id;
    }
  }

  onImageUploadFailed($event: any) {
  }

  onImageRemoving($event: RemovingEventArgs) {
  }

  onAfterImageDelete($event: AfterImageDeleteEventArgs) {
  }

  onBeforeQuickToolbarOpen($event: any) {
  }

  onQuickToolbarOpen($event: any) {
  }

  onQuickToolbarClose($event: any) {
  }

  contentChangedHandler() {
    const value = (this.editor as any).getUpdatedValue(); // or should it be this.editor.value
    this.executeOnChangeCommand(value);
  }

  onResize($event: UIEvent) {
  }

  onResizeStart($event: ResizeArgs) {
  }

  onResizing($event: ResizeArgs) {
  }

  onResizeStop($event: ResizeArgs) {
    this.executeOnChangeCommand(this.editor.value);
  }

  onValueChange($event: ChangeEventArgs) {
  }

  // mirror support

  private formatCode(code: string): string {
    return prettier.format(code, {
      parser: "html",
      plugins: [parserHtml],
      printWidth: 80,
      tabWidth: 2,
    });
  }

  // Uploader support

  public onAttachFile($event: ClickEventArgs) {
    this.uploaderDialog.visible = true;
  }

  public onShowEmojiPicker($event: ClickEventArgs) {
    // need to calculate X and Y positions
    this.editor.showEmojiPicker();
  }

  onUploaderClose($event: any) {
    this.uploader.clearAll();
  }

  // This is for attachment uploading
  public onUploaderSuccess($event: any) {

    // Retrieve the image id from the x-record-id header and use it as part of the src parameter of the img tag.
    const id = $event.response.headers.split("\r\n").filter((x) => x.includes("x-record-id"))[0].split(": ")[1];

    const originalFileName = $event.file.name;

    if ($event.element) {
      $event.element.src = "/attachment/" + id;
      $event.element.setAttribute('alt', $event.file.name);
      this.boundContentChangedHandler();
      $event.file.name = id;
    } else {
      $event.file.name = id;
    }
    const fileUrl = this.insertImageSettings.path + $event.file.name;

    const editor = this.editor;
    let selection: NodeSelection = new NodeSelection();
    let range: Range;
    let saveSelection: NodeSelection;

    (editor.contentModule.getEditPanel() as any).focus();
    range = selection.getRange(document);

    const nodes = selection.getSelectedNodes(document);

    // If inserting multiple we need to jump after the current inserted nodes <a></a> as
    // the #text of the <a> will be selected by default after 'createLink'
    // We wrap a <p> around the <a> to make it easier to manipulate in the editor.
    if (nodes.length == 1) {
      const node = nodes[0];
      if (node.parentNode && node.parentNode.nodeName == 'A') {
        const aElement = node.parentElement;
        const pElement = document.createElement("p");
        aElement.after(pElement);
          range.setStartAfter(pElement);
      }
    }

    saveSelection = selection.save(range, document);

    editor.executeCommand('createLink', {
        url: fileUrl,
        title: originalFileName,
        text: originalFileName,
        selection: saveSelection,
        target: "_blank"
      });
    editor.formatter.saveData();
    editor.formatter.enableUndo(editor);
  }

  onBeforePasteCleanup($event: any) {
  }

  onAfterPasteCleanup($event: any) {
  }

  onDialogOpen($event: BeforeOpenEventArgs) {
    // Ensure that dialogs do not get cut off with short editors.
    const elem = ($event.element as HTMLElement);
    elem.style.maxHeight = "fit-content";
    if (elem.clientHeight > elem.parentElement.clientHeight) {
      elem.style.height = elem.parentElement.clientHeight + "px";
    }
  }
}
